/**
 * The phone presentation of a region's workshops (BON-534).
 *
 * The design's mobile regional view is NOT a calendar: it is sub-region filter
 * chips above a card of workshop rows, over a window that reaches further
 * ahead on request. A month grid at 390px is unreadable, so this uses the
 * shared `WorkshopList` — the same card the design system uses wherever
 * workshops are read on a narrow screen.
 *
 * The chips are the only way to narrow the list here; there is no sub-region
 * tile grid on any viewport, which is what the design shows. They read and
 * write the page's hidden sub-regions, the same ones the desktop legend uses,
 * and the cancelled switch is the page's too, so neither resets with the layout.
 *
 * The chips and the rows want different things from the same field. A chip
 * reading "New England" is how you filter to the venues sitting directly in
 * the region, so the chips need every label. A row reading "New England" on
 * the New England page just repeats the breadcrumb, so the rows want it only
 * when the venue is in a sub-region. That split is a display choice, which is
 * why it lives here rather than in what the server sends.
 */
import { useMemo, type ReactNode } from 'react';

import { isCancelled, withoutCancelled } from '../../primitives/cancelled-workshops';
import { ShowCancelledToggle } from '../../primitives/ShowCancelledToggle';
import { useSubregionFilter } from '../../primitives/use-subregion-filter';
import { formatBlockDay } from '../../primitives/workshop-blocks';
import { WorkshopList } from '../../primitives/WorkshopList';
import type { WorkshopChip, WorkshopClickHandler } from '../../primitives/workshopsCalendarTypes';

import { hiddenForPick, pickedSubregion } from './phone-subregion-pick';
import { withoutViewedRegionLabel } from './region-workshops-shared';
import { RegionFilterChips } from './RegionFilterChips';

export interface RegionWorkshopsPhoneProps {
  workshops: WorkshopChip[];
  /** The last day loaded, `YYYY-MM-DD`; names how far an empty list looked. */
  through: string;
  /** The region being viewed. Rows in it are not labelled with its name; rows
   *  in a sub-region beneath it are. Omit to label every row. */
  viewedRegionSlug?: string;
  /** The page's hidden sub-regions, shared with the calendar. */
  hidden: string[];
  onHiddenChange: (hidden: string[]) => void;
  showCancelled: boolean;
  onToggleCancelled: () => void;
  onWorkshopClick?: WorkshopClickHandler;
  /** Shown under the rows, inside the card. */
  footer?: ReactNode;
}

function emptyLabelFor(only: string | null, anyHidden: boolean, through: string): string {
  const throughDay = formatBlockDay(through);
  if (only) return `No workshops in ${only} through ${throughDay}.`;
  if (anyHidden) return `No workshops in the sub-regions shown through ${throughDay}.`;
  return `No workshops scheduled through ${throughDay}.`;
}

export function RegionWorkshopsPhone({
  workshops,
  through,
  viewedRegionSlug,
  hidden,
  onHiddenChange,
  showCancelled,
  onToggleCancelled,
  onWorkshopClick,
  footer,
}: RegionWorkshopsPhoneProps) {
  const filter = useSubregionFilter(workshops, hidden);
  const only = pickedSubregion(filter);

  const shown = useMemo(
    () =>
      withoutViewedRegionLabel(
        showCancelled ? filter.shown : withoutCancelled(filter.shown),
        viewedRegionSlug,
      ),
    [filter.shown, showCancelled, viewedRegionSlug],
  );

  return (
    <>
      {filter.subregions.length > 0 && (
        <RegionFilterChips
          options={filter.subregions}
          selected={only}
          onSelect={(label) => onHiddenChange(hiddenForPick(filter, label))}
        />
      )}
      <section
        className="nb-card nb-card-tight"
        // Explicit padding: the card class alone left the heading
        // flush against the edges, and the first workshop tight under
        // them. Matches the week strip's own card padding so the two cards on a
        // page have the same inset.
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: '14px 16px 16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <h3 className="nb-display" style={{ margin: 0, fontSize: 14, letterSpacing: '.04em' }}>
            Workshops
          </h3>
        </div>
        <ShowCancelledToggle
          showing={showCancelled}
          count={filter.shown.filter(isCancelled).length}
          onToggle={onToggleCancelled}
        />
        <WorkshopList
          workshops={shown}
          onWorkshopClick={onWorkshopClick}
          emptyLabel={emptyLabelFor(only, filter.hidden.size > 0, through)}
        />
        {footer}
      </section>
    </>
  );
}
